/* eslint-disable jsx-a11y/anchor-is-valid */
import moment from "moment";
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useMount, useUpdateEffect } from "react-use";
import { useRecoilState } from "recoil";
import toast from "react-hot-toast";
import { getAttendanceList, updateAttendance } from "../api";
import {
  attendanceTitleState,
  attendanceCurriculumState,
  attendanceCurriculumListState,
  attendanceStudentListState,
  attendanceRecordState,
} from "../states/atoms/attendanceState";
import debounce from "../util/debounce";
import Icon from "../components/common/Icon";
import { autoHyphen } from "../util/common";

const AttendancePage = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [data, setQueryResult] = useState(null);
  const [record, setRecord] = useRecoilState(attendanceRecordState);
  const [title, setTitle] = useRecoilState(attendanceTitleState);
  const [{ currentIndex, currentItem }, setCurriculum] = useRecoilState(
    attendanceCurriculumState
  );
  const [student_list, setStudentList] = useRecoilState(
    attendanceStudentListState
  );
  const [curriculum, setCurriculumList] = useRecoilState(
    attendanceCurriculumListState
  );

  const getListQuery = async () => {
    const result = await queryClient.fetchQuery(["attendance/get/list"], {
      queryFn: () => getAttendanceList(id),
    });
    setQueryResult(result);
  };

  const { mutate: onUpdateAttendance } = useMutation({
    mutationFn: updateAttendance,
    onSettled: async (data, error) => {
      const { result } = data;
      let status = "success";
      let message = "";
      if (result) {
        status = data.data.status;
        message = data.data.message;
      }
      if (error) {
        status = error.status;
        message = error.message;
      }
      if (message) {
        toast.custom((t) => (
          <div
            className={`alert w-1/4 bg-white border border-zinc-100 text-bold ${
              t.visible ? "animate-enter" : "animate-leave"
            }
            }`}
          >
            <div>
              <Icon
                name="information-circle"
                className={`text-${status} w-10 h-10`}
              />
              <span className="text-sm">{message}</span>
            </div>
          </div>
        ));
      }
    },
  });

  const onChangeAttendance = useMemo(
    () => debounce((value) => onUpdateAttendance(value), 300),
    [onUpdateAttendance]
  );
  const onChangeDelayAttendance = useMemo(
    () => debounce((value) => onUpdateAttendance(value), 600),
    [onUpdateAttendance]
  );

  const handleAttendance = (sid, idx, score) => (e) => {
    const studentRecordList = [...record[sid]];
    const curList = studentRecordList[currentIndex].map((v, i) => {
      if (idx === 0) {
        if (i === idx) {
          if (v === 5) return 0;
          return 5;
        }
        if (i === 4) {
          return "";
        }
        return 0;
      }
      if (idx === 1 || idx === 2) {
        if (v === -1) {
          if (i === idx) return 0;
          return v;
        } else {
          if (i === 0) return 5;
          if (i === idx) return score;
          if (i === 3) return 0;
          return v;
        }
      }
      if (idx === 3) {
        if (v === 2) {
          return i > 3 ? "" : 0;
        } else {
          if (i < 3) return 0;
          if (i > 3) return v;
          return score;
        }
      }
      if (idx === 4 && i === idx) {
        return e.target.value;
      }
      return v;
    });
    studentRecordList.splice(currentIndex, 1, curList);
    setRecord({ ...record, [sid]: studentRecordList });
    const obj = {
      lecture_id: id,
      student_id: sid,
      record: studentRecordList,
      curriculumIndex: currentIndex,
    };
    if (idx === 4) {
      onChangeDelayAttendance(obj);
    } else {
      onChangeAttendance(obj);
    }
  };

  useUpdateEffect(() => {
    if (data.result) {
      const tmp = data.data;
      let curIdx = currentIndex;
      let curItem = currentItem;
      if (currentIndex < 0 && !currentItem) {
        curIdx = tmp.currentIndex;
        curItem =
          curIdx === tmp.curriculum.length
            ? null
            : { ...tmp.curriculum[tmp.currentIndex] };
      }
      const newList = tmp.student_list.reduce(
        (acc, { id, name, school, age, gender, s_tel, p_tel, certificate }) => {
          acc.push([
            { className: "py-1", value: name, label: "name", type: "value" },
            {
              className: "py-1",
              value: age,
              label: "age",
              type: "value",
            },
            {
              className: "py-1",
              value: gender,
              label: "gender",
              type: "value",
            },
            {
              className: "py-1",
              value: school,
              label: "school",
              type: "value",
            },
            {
              className: "py-1",
              value: autoHyphen(s_tel),
              label: "s_tel",
              type: "value",
            },
            {
              className: "py-1",
              value: autoHyphen(p_tel),
              label: "p_tel",
              type: "value",
            },
            {
              className: "px-[1px]",
              value: {
                type: "checkbox",
                className: "attendance bg-success",
                value: [id, 0, 5],
              },
              label: id,
              type: "input",
            },
            {
              className: "px-[1px]",
              value: {
                type: "checkbox",
                className: "attendance bg-accent",
                value: [id, 1, -1],
              },
              label: id,
              type: "input",
            },
            {
              className: "px-[1px]",
              value: {
                type: "checkbox",
                className: "attendance bg-warning",
                value: [id, 2, -1],
              },
              label: id,
              type: "input",
            },
            {
              className: "px-[1px]",
              value: {
                type: "checkbox",
                className: "attendance bg-secondary",
                value: [id, 3, 2],
              },
              label: id,
              type: "input",
            },
            {
              className: "py-1 px-4 align-top",
              value: {
                type: "text",
                className:
                  "w-full h-8 px-2 appearance-none bg-white border border-zinc-300 placeholder-zinc-400 rounded-lg shadow-sm text-sm",
                value: [id, 4, ""],
              },
              certificate,
              label: id,
              type: "input",
            },
          ]);
          return acc;
        },
        []
      );
      setTitle(tmp.title);
      if (currentIndex < 0 && !currentItem) {
        setCurriculum({
          currentItem: curItem,
          currentIndex: curIdx,
        });
      }
      setCurriculumList(tmp.curriculum);
      setStudentList(newList);
      const recordObj = tmp.student_list.reduce((acc, { id, attendance }) => {
        return {
          ...acc,
          [id]: attendance.list,
        };
      }, {});
      setRecord(recordObj);
    }
  }, [data]);

  useMount(() => {
    getListQuery();
  });

  const handleTabs = (e) => {
    const newIdx = Number(e.target.name.split("_")[1]);
    setCurriculum({
      currentItem:
        newIdx === curriculum.length ? null : { ...curriculum[newIdx] },
      currentIndex: newIdx,
    });
    getListQuery();
  };

  return (
    <div className="w-full mx-auto md:w-11/12 md:px-2">
      <h1 className="flex items-center px-2 py-4 font-sans text-xl font-bold break-normal text-cyan-500 md:text-2xl">
        {`${title} 출결 관리`}
      </h1>
      <div className="p-4 bg-white rounded shadow md:p-8 lg:mt-0">
        {currentItem && (
          <div className="inline-flex flex-wrap justify-between w-full pb-4">
            <h3 className="text-xl font-bold">
              {`${currentIndex + 1}차시. ${currentItem.content}`}
            </h3>
            <h3 className="text-xl">
              {new moment(currentItem.datetime.date).format("LL dddd")}
            </h3>
          </div>
        )}
        <div className="pb-2 overflow-scroll rounded-md scrollbar-hide">
          <div className="w-full overflow-scroll tabs flex-nowrap scrollbar-hide">
            {curriculum.map((_, i) => (
              <a
                className={`tab tab-md md:tab-lg tab-lifted ${
                  i === currentIndex && "tab-active"
                }`}
                key={`tabs_${i}`}
                name={`tabs_${i}`}
                onClick={handleTabs}
              >{`${i + 1}차시`}</a>
            ))}
            <a
              className={`tab tab-md md:tab-lg tab-lifted ${
                curriculum.length === currentIndex && "tab-active"
              }`}
              key={`tabs_${curriculum.length}`}
              name={`tabs_${curriculum.length}`}
              onClick={handleTabs}
            >{`수료`}</a>
          </div>
          <div className="overflow-x-auto border-[#C3C3C9] border-[1px] my-[-1px] rounded-lg rounded-t-none pb-2">
            {currentIndex === curriculum.length && (
              <table className="min-w-full py-4 overflow-hidden text-sm border-collapse rounded-lg rounded-t-none table-auto hover w-max">
                <thead className="bg-zinc-100 mb">
                  <tr className="text-center">
                    <th className="py-2"></th>
                    <th className="py-2 w-[6rem]">이름</th>
                    <th className="py-2 w-[2.5rem]">나이</th>
                    <th className="py-2 w-[2.5rem]">성별</th>
                    <th className="py-2 w-[9rem]">학교</th>
                    <th className="py-2 w-[8rem]">학생 전화번호</th>
                    <th className="py-2 w-[8rem]">학부모 전화번호</th>
                    <th className="py-2">수료증</th>
                  </tr>
                </thead>
                <tbody>
                  {student_list.map((litem, i) => (
                    <tr
                      className="text-center"
                      key={`row_${currentIndex}_${i}`}
                    >
                      <th className="py-1 w-7">{i + 1}</th>
                      {litem.length > 0 &&
                        litem.reduce(
                          (
                            acc,
                            { className, value, label, type, certificate },
                            j
                          ) => {
                            const key = `${label}_${currentIndex}_${i}_${j}`;
                            if (type === "value") {
                              return [
                                ...acc,
                                <td className={className} key={key}>
                                  {value}
                                </td>,
                              ];
                            }
                            if (
                              type === "input" &&
                              value.type === "text" &&
                              certificate
                            ) {
                              const [sid] = value.value;
                              const onClick = () =>
                                window.open(
                                  `/certificate/view?id=${certificate}&student_id=${sid}`,
                                  "_blank"
                                );
                              const className = "btn btn-primary btn-xs";
                              return [
                                ...acc,
                                <td key={key}>
                                  <button {...{ className, onClick }}>
                                    수료증
                                  </button>
                                </td>,
                              ];
                            }
                            return acc;
                          },
                          []
                        )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {currentIndex < curriculum.length && (
              <table className="min-w-full py-4 overflow-hidden text-sm border-collapse rounded-lg rounded-t-none table-auto hover w-max">
                <thead className="bg-zinc-100 mb">
                  <tr className="text-center">
                    <th className="py-2"></th>
                    <th className="py-2 w-[6rem]">이름</th>
                    <th className="py-2 w-[2.5rem]">나이</th>
                    <th className="py-2 w-[2.5rem]">성별</th>
                    <th className="py-2 w-[9rem]">학교</th>
                    <th className="py-2 w-[8rem]">학생 전화번호</th>
                    <th className="py-2 w-[8rem]">학부모 전화번호</th>
                    <th className="py-2">출석</th>
                    <th className="py-2">지각</th>
                    <th className="py-2">조퇴</th>
                    <th className="py-2">결석</th>
                    <th className="py-2">사유</th>
                  </tr>
                </thead>
                <tbody>
                  {student_list.map((litem, i) => (
                    <tr
                      className="text-center"
                      key={`row_${currentIndex}_${i}`}
                    >
                      <th className="py-1 w-7">{i + 1}</th>
                      {litem.length > 0 &&
                        litem.map(({ className, value, label, type }, j) => {
                          let defaultValue = 0;
                          const key = `${label}_${currentIndex}_${i}_${j}`;
                          if (
                            label &&
                            currentIndex > -1 &&
                            currentIndex < curriculum.length &&
                            label in record &&
                            record[label]
                          ) {
                            defaultValue = record[label][currentIndex][j - 6];
                          }
                          if (typeof defaultValue === "undefined") {
                            defaultValue = 0;
                          }
                          if (value && value.type === "checkbox") {
                            defaultValue = defaultValue !== 0;
                          }
                          return (
                            <td className={className} key={key}>
                              {type === "value" && value}
                              {type === "input" &&
                                value.type === "checkbox" && (
                                  <input
                                    type="checkbox"
                                    className={value.className}
                                    checked={defaultValue}
                                    onChange={handleAttendance(...value.value)}
                                  />
                                )}
                              {type === "input" && value.type === "text" && (
                                <input
                                  type="text"
                                  className={value.className}
                                  value={defaultValue || ""}
                                  onChange={handleAttendance(...value.value)}
                                />
                              )}
                            </td>
                          );
                        })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendancePage;
